import type { JSX } from "react";
import type { DayBandKind, DayBandSegment } from "../../domain/dayBand";
import { useI18n } from "../i18n/I18nContext";
import { cx } from "./classNames";
import styles from "./DayBand.module.css";

/** Properties of {@link DayBand}. */
export interface DayBandProps {
  /** The intervals of the day, positioned on the band (see `dayBandSegments`). */
  readonly segments: readonly DayBandSegment[];
  /** First full hour of the band. */
  readonly startHour: number;
  /** Hour at which the band ends. */
  readonly endHour: number;
  /** Position of the current time in percent, or `null` when the marker is not shown. */
  readonly nowPercent?: number | null | undefined;
}

/** Distance between two hours labelled under the band. */
const labelStep = 3;

/**
 * A position in percent as a CSS length.
 *
 * @param percent - Position from 0 to 100.
 * @returns The length, for example `37.5%`.
 */
function percentOf(percent: number): string {
  const length = `${String(percent)}%`;

  return length;
}

/** An hour labelled under the band with its position in percent. */
interface HourLabel {
  readonly hour: number;
  readonly left: number;
}

/**
 * The day band (F-43, ADR-0159): the intervals of a day on a line of hours, working time filled,
 * break time dashed, and the current time as a marker. The band is decorative: the figures are
 * in the text next to it, and the legend names every kind in words.
 *
 * @param props - Component properties.
 * @returns The band with its hours and legend.
 */
export function DayBand(props: DayBandProps): JSX.Element {
  const { t } = useI18n();
  const { segments, startHour, endHour } = props;
  const nowPercent = props.nowPercent ?? null;
  const span = endHour - startHour;
  const labels: HourLabel[] = [];

  for (let hour = startHour; hour <= endHour; hour += labelStep) {
    const left = ((hour - startHour) / span) * 100;
    labels.push({ hour, left });
  }

  const kinds: DayBandKind[] = [];
  for (const segment of segments) {
    if (!kinds.includes(segment.kind)) {
      kinds.push(segment.kind);
    }
  }

  const band = (
    <div className={styles["band"]}>
      <div className={styles["track"]} aria-hidden="true">
        {segments.map((segment, index) => (
          <span
            key={index}
            className={cx(styles["segment"], styles[segment.kind])}
            style={{ left: percentOf(segment.left), width: percentOf(segment.width) }}
          />
        ))}
        {nowPercent === null ? null : (
          <span className={styles["now"]} style={{ left: percentOf(nowPercent) }} />
        )}
      </div>
      <div className={styles["hours"]} aria-hidden="true">
        {labels.map((label) => (
          <span key={label.hour} className={styles["hour"]} style={{ left: percentOf(label.left) }}>
            {String(label.hour).padStart(2, "0")}
          </span>
        ))}
      </div>
      <ul className={styles["legend"]}>
        {kinds.map((kind) => (
          <li key={kind}>
            <span className={cx(styles["swatch"], styles[kind])} aria-hidden="true" />
            {t(`dayBand.kind.${kind}`)}
          </li>
        ))}
        {nowPercent === null ? null : (
          <li>
            <span className={styles["nowSwatch"]} aria-hidden="true" />
            {t("dayBand.now")}
          </li>
        )}
      </ul>
    </div>
  );

  return band;
}
